import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import {
  Cog6ToothIcon,
  BellIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  WrenchScrewdriverIcon,
  PhoneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  LinkIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";

// Import the separated components
import BusinessSettings from "../components/settings/BusinessSettings";
import ServicesSettings from "../components/settings/ServicesSettings";
import CalendarSettings from "../components/settings/CalendarSettings";
import CustomInstructionsSettings from "../components/settings/CustomInstructionsSettings";
import PaymentSettings from "../components/settings/PaymentSettings";
import EscalationSettings from "../components/settings/EscalationSettings";
import NotificationSettings from "../components/settings/NotificationSettings";

// Fix: Use consistent environment variable
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://sayhi-voice-engine-production.up.railway.app";

export default function Settings() {
  const { userBusinesses, isSuperAdmin, user } = useAuth();
  const [activeTab, setActiveTab] = useState("business");
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [error, setError] = useState(null);

  const [settings, setSettings] = useState({
    business_name: "",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    staff_phone: "",
    address: "",
    business_type: "",
    service_area: "",
    hours: "",
    services: [],
    emergency_keywords: [],
    emergency_service: false,
    calendar_connected: false,
    calendar_provider: null,
    calendar_email: null,
    calendar_name: null,
    start_time: "09:00",
    end_time: "17:00",
    buffer_minutes: 15,
    default_duration: 60,
    working_days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
    timezone: "America/New_York",
    auto_booking_enabled: true,
    require_confirmation: false,
    emailNotifications: true,
    emergencyAlerts: true,
    is_escalate_phone: false,
    escalate_phone_number: "",
    escalate_sms_notifications: true,
    escalate_email_notifications: true,
    custom_instructions: "",
    conversation_highlights: "",
    payment_methods: ["cash", "check", "card"],
    pricing_info: "",
    deposit_required: false,
    deposit_amount: "",
    deposit_percentage: null,
    deposit_threshold_amount: null,
    deposit_policy: "",
  });

  const myBusiness = userBusinesses?.[0]?.business_phone;

  const tabs = [
    { id: "business", name: "Business Info", icon: BuildingOfficeIcon },
    { id: "services", name: "Services", icon: WrenchScrewdriverIcon },
    { id: "calendar", name: "Calendar", icon: CalendarIcon },
    {
      id: "custom_instructions",
      name: "Custom Instructions",
      icon: ChatBubbleLeftRightIcon,
    },
    { id: "payment", name: "Payment & Pricing", icon: CreditCardIcon },
    { id: "escalation", name: "Escalation", icon: ArrowPathIcon },
    { id: "notifications", name: "Notifications", icon: BellIcon },
  ];

  useEffect(() => {
    if (myBusiness) {
      loadBusinessSettings();
    }
  }, [myBusiness]);

  useEffect(() => {
    if (businessInfo?.id) {
      checkCalendarStatus();
    }
  }, [businessInfo]);

  // Fix: Use localStorage token method like other components
  const getAuthToken = () => {
    return localStorage.getItem('supabase_token');
  };

  const loadBusinessSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('No authentication token available');
      }

      console.log('🌐 Frontend: Loading business settings for:', myBusiness);
      console.log('🌐 Frontend: Using backend URL:', API_BASE_URL);

      // Use existing dashboard endpoint
      const response = await fetch(`${API_BASE_URL}/api/dashboard/business-settings/${myBusiness}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('📡 Frontend: Business settings response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Frontend: Business settings API error:', errorText);
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📊 Frontend: Business settings data:', data);
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to load business settings');
      }

      const businessData = data.business;

      console.log("📋 Business data loaded:", businessData);
      console.log("📋 Business ID for calendar:", businessData?.id);
      console.log("📋 Emergency service from DB:", businessData?.emergency_service);

      setBusinessInfo(businessData);
      setSettings({
        business_name: businessData?.business_name || "",
        owner_name: businessData?.owner_name || "",
        owner_email: businessData?.owner_email || "",
        owner_phone: businessData?.owner_phone || "",
        staff_phone: businessData?.staff_phone || "",
        address: businessData?.address || "",
        business_type: businessData?.business_type || "",
        service_area: businessData?.service_area || "",
        hours: businessData?.hours || "",
        services: businessData?.services || [],
        emergency_keywords: businessData?.emergency_keywords || [],
        emergency_service: businessData?.emergency_service || false,
        calendar_connected: businessData?.nylas_grant_id ? true : false,
        calendar_provider: businessData?.calendar_provider || null,
        calendar_email: businessData?.calendar_email || null,
        calendar_name: businessData?.calendar_name || null,
        start_time: businessData?.start_time || "09:00",
        end_time: businessData?.end_time || "17:00",
        buffer_minutes: businessData?.buffer_minutes || 15,
        default_duration: businessData?.default_appointment_duration || 60,
        working_days: businessData?.working_days || [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
        ],
        timezone: businessData?.timezone || "America/New_York",
        auto_booking_enabled: businessData?.auto_booking_enabled ?? true,
        require_confirmation: businessData?.require_confirmation ?? false,
        emailNotifications: true,
        emergencyAlerts: true,
        is_escalate_phone: businessData?.is_escalate_phone || false,
        escalate_phone_number: businessData?.escalate_phone_number || "",
        escalate_sms_notifications:
          businessData?.escalate_sms_notifications ?? true,
        escalate_email_notifications:
          businessData?.escalate_email_notifications ?? true,
        custom_instructions: businessData?.custom_instructions || "",
        conversation_highlights: businessData?.conversation_highlights || "",
        payment_methods: businessData?.payment_methods || [
          "cash",
          "check",
          "card",
        ],
        pricing_info: businessData?.pricing_info || "",
        deposit_required: businessData?.deposit_required || false,
        deposit_amount: businessData?.deposit_amount || "",
        deposit_percentage: businessData?.deposit_percentage || null,
        deposit_threshold_amount:
          businessData?.deposit_threshold_amount || null,
        deposit_policy: businessData?.deposit_policy || "",
      });

      console.log('✅ Frontend: Business settings loaded successfully');
    } catch (error) {
      console.error("❌ Frontend: Error loading business settings:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkCalendarStatus = async () => {
    const businessId =
      businessInfo?.id || businessInfo?.business_id || myBusiness;
    if (!businessId) {
      console.log("No business ID available for calendar status check");
      return;
    }

    console.log("Checking calendar status for business:", businessId);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/calendar/status/${businessId}`
      );
      console.log(
        "Calendar status response:",
        response.status,
        response.statusText
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Calendar status error:", errorText);
        return;
      }

      const data = await response.json();
      console.log("Calendar status data:", data);

      if (data.connected) {
        setSettings((prev) => ({
          ...prev,
          calendar_connected: true,
          calendar_provider: data.provider,
          calendar_email: data.email,
          calendar_name: data.calendar_name,
          start_time: data.settings.start_time || "09:00",
          end_time: data.settings.end_time || "17:00",
          buffer_minutes: data.settings.buffer_minutes || 15,
          default_duration: data.settings.default_duration || 60,
          working_days: data.settings.working_days || [
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday",
          ],
          timezone: data.settings.timezone || "America/New_York",
        }));
      }
    } catch (error) {
      console.error("Error checking calendar status:", error);
    }
  };

  const testCalendar = async () => {
    const businessId =
      businessInfo?.id || businessInfo?.business_id || myBusiness;
    if (!businessId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/calendar/test/${businessId}`
      );
      const data = await response.json();

      if (response.ok && data.success) {
        setSaveMessage(
          `Calendar test successful! Found ${data.events_count} upcoming events.`
        );
      } else {
        setSaveMessage(
          "Calendar test failed: " + (data.error || "Unknown error")
        );
      }
    } catch (error) {
      console.error("Error testing calendar:", error);
      setSaveMessage("Calendar test failed: " + error.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSaveMessage(""), 5000);
    }
  };

  const saveCalendarSettings = async () => {
    const businessId =
      businessInfo?.id || businessInfo?.business_id || myBusiness;
    if (!businessId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/calendar/settings/${businessId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            start_time: settings.start_time,
            end_time: settings.end_time,
            buffer_minutes: settings.buffer_minutes,
            default_duration: settings.default_duration,
            working_days: settings.working_days,
            timezone: settings.timezone,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to save calendar settings");
      }

      setSaveMessage("Calendar settings saved successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      console.error("Error saving calendar settings:", error);
      setSaveMessage("Failed to save calendar settings: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Memoize the input change handler
  const handleInputChange = useCallback((key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const saveSettings = async () => {
    if (!myBusiness) return;

    setLoading(true);
    setSaveMessage("");
    setError(null);
    
    try {
      const authToken = getAuthToken();
      
      if (!authToken) {
        throw new Error('No authentication token available');
      }

      const updateData = {
        business_name: settings.business_name,
        owner_name: settings.owner_name,
        owner_email: settings.owner_email,
        owner_phone: settings.owner_phone,
        staff_phone: settings.staff_phone,
        address: settings.address,
        business_type: settings.business_type,
        service_area: settings.service_area,
        hours: settings.hours,
        services: settings.services,
        emergency_keywords: settings.emergency_keywords,
        emergency_service: settings.emergency_service,
        start_time: settings.start_time,
        end_time: settings.end_time,
        buffer_minutes: settings.buffer_minutes,
        default_appointment_duration: settings.default_duration,
        working_days: settings.working_days,
        timezone: settings.timezone,
        auto_booking_enabled: settings.auto_booking_enabled,
        require_confirmation: settings.require_confirmation,
        pricing_info: settings.pricing_info,
        is_escalate_phone: settings.is_escalate_phone,
        escalate_phone_number: settings.escalate_phone_number,
        escalate_sms_notifications: settings.escalate_sms_notifications,
        escalate_email_notifications: settings.escalate_email_notifications,
        custom_instructions: settings.custom_instructions,
        conversation_highlights: settings.conversation_highlights,
        payment_methods: settings.payment_methods,
        deposit_required: settings.deposit_required,
        deposit_amount: settings.deposit_amount,
        deposit_percentage: settings.deposit_percentage,
        deposit_threshold_amount: settings.deposit_threshold_amount,
        deposit_policy: settings.deposit_policy,
      };

      console.log("💾 Frontend: Saving settings with emergency service:", settings.emergency_service);
      console.log("🌐 Frontend: Using backend URL:", API_BASE_URL);

      // Use existing dashboard endpoint  
      const response = await fetch(`${API_BASE_URL}/api/dashboard/business-settings/${myBusiness}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      console.log('📡 Frontend: Save settings response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Frontend: Save settings API error:', errorText);
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('📊 Frontend: Save settings response data:', data);
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to save settings');
      }

      console.log('✅ Frontend: Settings saved successfully');
      setSaveMessage("Settings saved successfully!");
      setTimeout(() => setSaveMessage(""), 3000);
    } catch (error) {
      console.error("❌ Frontend: Error saving settings:", error);
      setSaveMessage("Failed to save settings: " + error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const connectNylasCalendar = async () => {
    const businessId =
      businessInfo?.id || businessInfo?.business_id || myBusiness;

    console.log("🔍 Frontend business ID debug:", {
      businessInfo_id: businessInfo?.id,
      businessInfo_business_id: businessInfo?.business_id,
      myBusiness: myBusiness,
      finalBusinessId: businessId,
      businessInfoObject: businessInfo,
    });

    if (!businessId) {
      setSaveMessage("Business information not loaded. Please try again.");
      return;
    }

    console.log("Connecting calendar for business:", businessId);
    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/calendar/connect`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            business_id: businessId,
            redirect_uri: `${API_BASE_URL}/api/calendar/callback`,
          }),
        }
      );

      console.log("Connect response:", response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Connect error response:", errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log("Connect response data:", data);

      if (!data.auth_url) {
        throw new Error("No authorization URL received from server");
      }

      const popup = window.open(
        data.auth_url,
        "nylas-auth",
        "width=600,height=700,scrollbars=yes"
      );

      if (!popup) {
        throw new Error("Popup blocked. Please allow popups for this site.");
      }

      const checkClosed = setInterval(async () => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setLoading(false);
          await checkCalendarStatus();
          loadBusinessSettings();
        }
      }, 1000);

      const messageHandler = async (event) => {
        if (
          !event.origin.includes("railway.app") &&
          !event.origin.includes("up.railway.app")
        ) {
          console.log("🔍 Ignoring message from origin:", event.origin);
          return;
        }

        console.log("🔍 Received message:", event.data);

        if (event.data.type === "CALENDAR_CONNECTED") {
          popup.close();
          clearInterval(checkClosed);
          window.removeEventListener("message", messageHandler);
          setLoading(false);

          setSaveMessage("Calendar connected successfully!");
          setTimeout(() => setSaveMessage(""), 3000);

          await checkCalendarStatus();
          loadBusinessSettings();
        } else if (event.data.type === "CALENDAR_ERROR") {
          popup.close();
          clearInterval(checkClosed);
          window.removeEventListener("message", messageHandler);
          setLoading(false);

          setSaveMessage("Calendar connection failed: " + event.data.error);
          setTimeout(() => setSaveMessage(""), 5000);
        }
      };

      window.addEventListener("message", messageHandler);
    } catch (error) {
      console.error("Error connecting calendar:", error);
      setSaveMessage("Failed to connect calendar: " + error.message);
      setLoading(false);
    }
  };

  const disconnectCalendar = async () => {
    const businessId =
      businessInfo?.id || businessInfo?.business_id || myBusiness;

    if (!businessId) {
      setSaveMessage("Business information not loaded. Please try again.");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to disconnect your calendar? This will disable automatic appointment booking."
      )
    ) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/calendar/${businessId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Disconnect response:", response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Disconnect error response:", errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log("Disconnect response data:", data);

      setSettings((prev) => ({
        ...prev,
        calendar_connected: false,
        calendar_provider: null,
        calendar_email: null,
        calendar_name: null,
      }));

      setSaveMessage("Calendar disconnected successfully!");
      setTimeout(() => setSaveMessage(""), 3000);

      await loadBusinessSettings();
    } catch (error) {
      console.error("Error disconnecting calendar:", error);
      setSaveMessage("Failed to disconnect calendar: " + error.message);
      setTimeout(() => setSaveMessage(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Calendar action handlers (memoized)
  const handleConnectCalendar = useCallback(connectNylasCalendar, [
    businessInfo,
    myBusiness,
  ]);

  const handleDisconnectCalendar = useCallback(disconnectCalendar, [
    businessInfo,
    myBusiness,
  ]);

  const handleTestCalendar = useCallback(testCalendar, [
    businessInfo,
    myBusiness,
  ]);

  const handleSaveCalendarSettings = useCallback(saveCalendarSettings, [
    businessInfo,
    myBusiness,
    settings,
  ]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      const number = cleaned.slice(1);
      return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
    }
    return phone;
  };

  if (isSuperAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Admin Access
          </h2>
          <p className="text-gray-600">
            Settings are managed per business. Select a specific business from
            the main dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (!myBusiness) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            No Business Access
          </h2>
          <p className="text-gray-600">
            You don't have access to any businesses. Contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Settings</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadBusinessSettings}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    const commonProps = {
      settings,
      onInputChange: handleInputChange,
      loading,
    };

    switch (activeTab) {
      case "business":
        return <BusinessSettings {...commonProps} />;
      case "services":
        return <ServicesSettings {...commonProps} />;
      case "calendar":
        return (
          <CalendarSettings
            {...commonProps}
            onConnect={handleConnectCalendar}
            onDisconnect={handleDisconnectCalendar}
            onTest={handleTestCalendar}
            onSaveCalendarSettings={handleSaveCalendarSettings}
          />
        );
      case "custom_instructions":
        return <CustomInstructionsSettings {...commonProps} />;
      case "payment":
        return <PaymentSettings {...commonProps} />;
      case "escalation":
        return (
          <EscalationSettings
            {...commonProps}
            myBusiness={myBusiness}
            API_BASE_URL={API_BASE_URL}
          />
        );
      case "notifications":
        return <NotificationSettings {...commonProps} />;
      default:
        return <BusinessSettings {...commonProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white shadow-lg border-b">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Cog6ToothIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Business Settings
                </h1>
                <p className="text-gray-600">
                  Configure your AI assistant and business preferences
                </p>
              </div>
            </div>

            {saveMessage && (
              <div
                className={`px-4 py-2 rounded-lg text-sm ${
                  saveMessage.includes("Failed") ||
                  saveMessage.includes("failed")
                    ? "bg-red-100 text-red-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {saveMessage}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:w-64 flex-shrink-0">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-lg p-6">
              {loading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="h-5 w-5 text-blue-600 animate-spin" />
                    <span className="text-blue-600">Saving...</span>
                  </div>
                </div>
              )}

              {renderContent()}

              {activeTab !== "calendar" && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex justify-end">
                    <button
                      onClick={saveSettings}
                      disabled={loading}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                    >
                      <span>Save Settings</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}